import { element } from '../../global/template-literals';
import { UIElement } from '../ui-element';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import type { UIHeaderTypes } from './ui-header.type';
import styles from './ui-header.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIHeader
 * @element ui-header
 * @classdesc Represents a class for <code>ui-header</code> element.
 * @property {UIHeaderType} [type="default"] {@attr type} Type of header.
 * @property {UIHeaderToolbar} sidebar Shortcut to UIHeaderToolbar child element.
 * @property {UIHeaderContent} content Shortcut to UIHeaderContent child element.
 * @example
 * <ui-header>
 * </ui-header>
 */
@Component({ tag: 'ui-header', styles })
export class UIHeader extends UIElement {
    /**
     * @private
     * @returns {UIHeaderTypes}
     */
    static readonly Type: UIHeaderTypes = {
        Default: 'default',
        Box: 'box',
        Fdp: 'fdp',
    } as const;

    /**
     * List of allowed header children
     */
    static readonly allowedHeaderTags: Array<string> = [
        'ui-header-toolbar',
        'ui-header-content',
        'ui-header-row',
    ] as const;

    @Attribute(String) type: string = UIHeader.Type.Default;

    @Child('ui-header-toolbar') toolbar?: HTMLElement;
    @Child('ui-header-content') content?: HTMLElement;

    /**
     * @inheritDoc
     */
    override render(): void {
        const headerNodes: Array<HTMLElement> = this.detachChildNodes().filter(
            (node: Element) => {
                return (
                    node.localName &&
                    UIHeader.allowedHeaderTags.includes(node.localName)
                );
            }
        );

        this.append(
            element`
            <header role="banner">
              ${headerNodes}
            </header>
            `
        );
    }
}
